import React, { createContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios, { setUnauthorizedHandler, clearAxiosCache } from "../axios-config";

export interface UserProfile {
    userId: string | null;
    name: string | null;
    picture?: string;
    avatar?: string | null;
    theme?: string | null;
    customCss?: string | null;
    email: string | null;
    org: string | null;
    orgId: string | null;
    mainRole: string | null;
    store: string | null;
    storeName?: string | null;
    grade?: number | null;  
    teacher?: string | null;
    className: string | null;
    points: number | null;
    general: number | null; // New field for General Account balance
    savings: number | null;
    monthlyEarnings: number | null; // New field for monthly earnings
    acornsRedeemed: number | null;
    pendingPickups: any[]; //TODO: Define type
    jobs: any[]; //TODO: Define type
    claims: any;
    students?: any[]; //TODO: Define type
    teachers?: any[]; //TODO: Define type
    hallpasses?: any[]; //TODO: Define type
    orders?: any[]; //TODO: Define type
    altId?: string;
    badges?: {
        badgeId: string;
        badgeName?: string;
        badgeDescription?: string;
        currentXp?: number;
        levelNumber: number;
        levelName?: string;
        badgeImageUrl?: string;
        levelImageUrl?: string;
    }[];
}

interface UserContextType {
    userProfile: UserProfile | null;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
    fetchUserProfile: () => Promise<UserProfile | null>;
    handleLogout: (event: React.FormEvent) => void;
    isLoading: boolean;
    isInitialized: boolean;
    isImpersonating: boolean;
    impersonatedUser: { id: string; email: string; fullName: string; role: string } | null;
    setImpersonationState: (isImpersonating: boolean, user?: { id: string; email: string; fullName: string; role: string }) => void;
    isAuthenticated: boolean;
    refreshProfile: () => Promise<void>;
    hasClaim: (claimType: string, claimValue?: string) => boolean; // NEW: Check a user claim
}

export const UserContext = createContext<UserContextType>({
    userProfile: null,
    setUserProfile: () => { },
    fetchUserProfile: async () => null,
    handleLogout: () => { },
    isLoading: false,
    isInitialized: false,
    isAuthenticated: true,
    refreshProfile: async () => { },
    hasClaim: () => false, // Default implementation
    isImpersonating: false,
    impersonatedUser: null,
    setImpersonationState: () => { },
});

interface Props {
    children: ReactNode;
}

export const UserProvider: React.FC<Props> = ({ children }) => {
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [impersonatedUser, setImpersonatedUser] = useState<{ id: string; email: string; fullName: string; role: string } | null>(null);
    const isInitializedRef = useRef(false);
    const navigate = useNavigate();

    // Detect if Google sign-in cookies are present on this origin
    const hasGoogleSignInCookie = useCallback((): boolean => {
        if (typeof document === 'undefined') return false;
        const cookieNames = document.cookie
            .split(';')
            .map((c) => c.trim().split('=')[0]);
        // Common cookies set by Google sign-in / One Tap on first-party origin
        // G_ENABLED_IDPS=google, g_state, and G_AUTHUSER_* variants
        return cookieNames.some(
            (name) =>
                name === 'G_ENABLED_IDPS' ||
                name === 'g_state' ||
                name.startsWith('G_AUTHUSER')
        );
    }, []);

    const setImpersonationState = useCallback((isImp: boolean, user?: { id: string; email: string; fullName: string; role: string }) => {
        setIsImpersonating(isImp);
        setImpersonatedUser(user || null);
    }, []);

    const handleLogout = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            await axios.post('/api/account/signout', {
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });
            // Clear any cached responses (like /api/profile) to avoid stale data on next login
            try { clearAxiosCache(); } catch {}
            setUserProfile(null);
            // Reset initialization flags to allow re-fetch on next login
            isInitializedRef.current = false;
            setIsInitialized(false);
            // Mark an intentional logout to avoid auto-redirecting to Google on next 401
            try {
                sessionStorage.setItem('acorn:recentLogout', '1');
            } catch {}
            // Navigate to home page after successful logout
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    // Handle session expiry (401 responses)
    const handleSessionExpired = useCallback(() => {
        console.log('Session expired, clearing user state');
        setUserProfile(null);
        isInitializedRef.current = false;
        setIsInitialized(false);
        setIsImpersonating(false);
        setImpersonatedUser(null);

        // Clear any cached responses to ensure fresh fetches after re-auth
        try { clearAxiosCache(); } catch {}

        // If the user intentionally logged out recently, honor that and show the login page
        try {
            const wasRecentLogout = sessionStorage.getItem('acorn:recentLogout');
            if (wasRecentLogout) {
                sessionStorage.removeItem('acorn:recentLogout');
                navigate('/login', { replace: true });
                return;
            }
        } catch {}

        // Decide where to send the user on 401 based on presence of Google cookies
        const currentPath = window.location.pathname + window.location.search;
        const returnUrl = encodeURIComponent(window.location.origin + currentPath);
        const apiBaseUrl = process.env.API_URL || process.env.VITE_API_URL;

        // If user is on the homepage or already on the login/signup page, don't auto-redirect.
        const path = window.location.pathname;
        const isHome = path === '/' || path === '';
        const isAuthPage = path.startsWith('/login') || path.startsWith('/signup');

        if (isHome || isAuthPage) {
            return;
        }

        // Check preferred auth (if user previously chose Google) in addition to cookies
        let prefersGoogle = false;
        try {
            prefersGoogle = (localStorage.getItem('acorn:preferredAuth') || '').toLowerCase() === 'google';
        } catch {}

        // Fallback preference from environment when we can't detect user preference
        const envPreferGoogle = String((process.env.VITE_PREFER_GOOGLE_ON_401 as any) ?? '').toLowerCase() === 'true'
            || String((import.meta as any)?.env?.VITE_PREFER_GOOGLE_ON_401 ?? '').toLowerCase() === 'true';
        // If user is heading to likely student/shop routes, prefer Google by default
        const isLikelyGoogleAudience = path.startsWith('/shop') || path.startsWith('/student') || path.startsWith('/acorn');
        const preferGoogleDefault = envPreferGoogle || isLikelyGoogleAudience;

        if (prefersGoogle || hasGoogleSignInCookie() || preferGoogleDefault) {
            const base = apiBaseUrl ?? '';
            window.location.href = `${base}/api/account/signingoogle?returnUrl=${returnUrl}`;
        } else {
            // Default to in-app login for non-Google or unknown providers
            navigate('/login', { replace: true });
        }
    }, [hasGoogleSignInCookie, navigate]);

    const fetchUserProfile = useCallback(async () => {
        if (isLoading || isInitializedRef.current) {
            console.log("Skipping fetchUserProfile: Already loading or initialized");
            return userProfile;
        }

        console.log("Fetching user profile...");
        setIsLoading(true);

        try {
            // Bust both browser and in-memory axios cache with a timestamp param
            const response = await axios.get("/api/profile", {
                params: { _: Date.now() }
            });
            console.log("User profile fetched:", response.data);
            setUserProfile(response.data);
            isInitializedRef.current = true;
            return response.data;
        } catch (error) {
            console.log("Failed to fetch user profile ", error);
            setUserProfile(null);
            return null;
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
            console.log("fetchUserProfile complete");
        }
    }, []);

    const refreshProfile = useCallback(async () => {
        try {
            const response = await axios.get("/api/profile", { params: { _: Date.now() } });
            if (response.status === 200) {
                setUserProfile(response.data);
            }
        } catch (error) {
            console.error("Failed to refresh user profile", error);
        }
    }, []);

    const hasClaim = (claimType: string, claimValue = 'True'): boolean => {
        if (!Array.isArray(userProfile?.claims)) return false;

        return userProfile.claims.some((c: any) => {
            const type = c.Type ?? c.type ?? '';
            const value = c.Value ?? c.value ?? '';
            return type === claimType && String(value) === String(claimValue);
        });
    };

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]); // Run only once on mount

    // Register the session expiry handler with axios interceptor
    useEffect(() => {
        setUnauthorizedHandler(handleSessionExpired);
    }, [handleSessionExpired]);

    return (
        <UserContext.Provider value={{
            userProfile,
            setUserProfile,
            fetchUserProfile,
            handleLogout,
            isLoading,
            isInitialized,
            isAuthenticated: !!userProfile,
            refreshProfile,
            hasClaim, // NEW: Added the hasClaim method to context
            isImpersonating,
            impersonatedUser,
            setImpersonationState
        }}>
            {children}
        </UserContext.Provider>
    );
};